import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../Button';
import { services } from '../data/services';
import { Section, Container, WhatsAppButton, PHONE_DISPLAY, PHONE_HREF, WHATSAPP_URL } from '../shared/ui';

const AngebotSection = styled(Section)`
  background: linear-gradient(120deg, #e6edfa 0%, #fff 100%);
  border-radius: 24px;
  box-shadow: 0 4px 32px rgba(0,52,196,0.10);
  margin-bottom: 2rem;
  padding: 3rem 0;
`;
const AngebotTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: var(--brand);
  margin-bottom: 1rem;
  text-align: center;
`;
const AngebotFormBox = styled.div`
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 2px 16px rgba(0,52,196,0.08);
  padding: 2.5rem 2rem;
  max-width: 500px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;
const AngebotDesc = styled.p`
  text-align: center;
  color: #3a4662;
  margin-bottom: 2rem;
`;

const fieldStyle: React.CSSProperties = {
  width: '100%',
  // Ohne border-box addiert der Browser Padding und Rahmen auf die 100 % und
  // die Felder werden unterschiedlich breit - das Projekt hat keinen globalen
  // box-sizing-Reset, deshalb hier explizit.
  boxSizing: 'border-box',
  padding: '0.7em',
  borderRadius: 10,
  border: '1px solid #e6edfa',
  marginTop: 6,
  // 16px verhindert, dass iOS Safari beim Fokussieren in das Feld hineinzoomt.
  fontSize: '16px',
  minHeight: 44,
};
const labelStyle: React.CSSProperties = { fontWeight: 500, color: 'var(--brand)' };

const StatusMessage = styled.div<{ $variant: 'success' | 'error' }>`
  padding: 0.9em 1.1em;
  border-radius: 12px;
  font-size: 0.98rem;
  text-align: center;
  background: ${p => (p.$variant === 'success' ? '#e7f6ec' : '#fdecec')};
  color: ${p => (p.$variant === 'success' ? '#1b7a3d' : '#b3261e')};
  border: 1px solid ${p => (p.$variant === 'success' ? '#b6e3c5' : '#f5c2c0')};
`;
// Kontaktlinks innerhalb einer Statusmeldung. color: inherit haelt sie im
// Farbton der Box, damit sie auf dem roten Hintergrund lesbar bleiben.
const StatusLink = styled.a`
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
  white-space: nowrap;
`;
const PrivacyRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.98em;
  color: #3a4662;
  margin: 8px 0;
`;
const PrivacyLink = styled(Link)`
  color: var(--brand);
  font-weight: 600;
`;
const Range = styled.input`
  width: 100%;
  accent-color: var(--brand);
  cursor: pointer;
  margin-top: 10px;
`;
const RangeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 6px;
`;
const NumberField = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  font-weight: 800;
  color: var(--brand);
`;
const NumberInput = styled.input`
  width: 92px;
  padding: 0.35em 0.5em;
  border: 1px solid #d5e0f2;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  color: var(--brand);
  text-align: right;
  &:focus {
    outline: none;
    border-color: var(--brand);
    box-shadow: 0 0 0 3px rgba(0, 52, 196, 0.12);
  }
`;
const RangeHint = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  /* siehe Preisrechner: #8593ad erreicht auf Weiss nur 3,1:1 statt 4,5:1 */
  color: var(--ink-soft);
  margin-top: 2px;
`;

type SubmitState = 'idle' | 'sending' | 'success' | 'error' | 'ratelimited' | 'invalid';

const Kontakt: React.FC<{
  title?: string;
  description?: string;
  /** Ueberschriftenebene: 'h1', wenn dieser Abschnitt die Hauptueberschrift der Seite ist. */
  headingLevel?: 'h1' | 'h2';
}> = ({
  title = 'Kostenlose Besichtigung anfragen',
  description = 'Sie möchten wissen, welche Reinigungslösung zu Ihrem Objekt passt? Fragen Sie eine kostenlose Besichtigung an. Danach erhalten Sie ein transparentes Festpreisangebot passend zu Ihren Räumen, Öffnungszeiten und Anforderungen.',
  headingLevel = 'h2',
}) => {
  const [status, setStatus] = React.useState<SubmitState>('idle');
  const [serverError, setServerError] = React.useState('');

  const [searchParams] = useSearchParams();
  const prefLeistung = searchParams.get('leistung') || '';
  const prefFlaeche = searchParams.get('flaeche') || '';
  const prefHaeufigkeit = searchParams.get('haeufigkeit') || '';
  const prefPreis = searchParams.get('preis') || '';
  const prefillNachricht = prefLeistung
    ? `Anfrage über den Preisrechner:\n- Leistung: ${prefLeistung}\n- Fläche: ca. ${prefFlaeche} m²\n- Häufigkeit: ${prefHaeufigkeit}\n- Geschätzte Kosten: ${prefPreis}\n\nBitte um ein unverbindliches Angebot.`
    : '';

  const [objektgroesse, setObjektgroesse] = React.useState<number>(() => {
    const n = Number(prefFlaeche);
    return Number.isFinite(n) && n >= 20 ? Math.min(n, 1000) : 80;
  });

  const leistungTitles = services.map((s) => s.title);
  const leistungOptions = prefLeistung && !leistungTitles.includes(prefLeistung)
    ? [prefLeistung, ...leistungTitles]
    : leistungTitles;

  const tageBasis = [
    ...[1, 2, 3, 4, 5, 6, 7].map((d) => `${d} ${d === 1 ? 'Tag' : 'Tage'}/Woche`),
    '14-täglich',
    'Monatlich',
    'Einmalig / nach Bedarf',
  ];
  const tageOptions = prefHaeufigkeit && !tageBasis.includes(prefHaeufigkeit)
    ? [prefHaeufigkeit, ...tageBasis]
    : tageBasis;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const praxisname = formData.get('praxisname')?.toString() || '';
    const ansprechpartner = formData.get('ansprechpartner')?.toString() || '';
    const ort = formData.get('ort')?.toString() || '';
    const leistung = formData.get('leistung')?.toString() || '';
    const nachricht = formData.get('nachricht')?.toString() || '';
    const privacyAccepted = formData.get('privacy') === 'on';
    const data = {
      praxisname,
      ansprechpartner,
      ort,
      leistung,
      telefon: formData.get('telefon')?.toString() || '',
      email: formData.get('email')?.toString() || '',
      praxisgroesse: `${objektgroesse} m²`,
      reinigungstage: formData.get('reinigungstage')?.toString() || '',
      nachricht,
      // Kompatibilität mit bestehendem Backend-Mailtemplate
      name: ansprechpartner || praxisname,
      adresse: [praxisname, ort].filter(Boolean).join(', '),
      beschreibung: nachricht,
      privacyAccepted,
      hp: formData.get('hp')?.toString() || '',
    };

    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setObjektgroesse(80);
      } else if (res.status === 429) {
        setStatus('ratelimited');
      } else if (res.status === 400) {
        // Der Server weiss genauer, was fehlt (z. B. ungueltige E-Mail) als
        // die pauschale Fehlermeldung. Faellt das Auslesen aus, bleibt es beim
        // allgemeinen Hinweis mit Telefon und WhatsApp.
        const body = await res.json().catch(() => null);
        if (body?.error) {
          setServerError(body.error as string);
          setStatus('invalid');
        } else {
          setStatus('error');
        }
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const sending = status === 'sending';

  return (
    <AngebotSection id="kontakt">
      <Container>
        <AngebotTitle as={headingLevel}>{title}</AngebotTitle>
        <AngebotDesc>{description}</AngebotDesc>
        <AngebotFormBox>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
            <label style={labelStyle}>Firma / Objekt*
              <input type="text" name="praxisname" required autoComplete="organization" style={fieldStyle} />
            </label>
            <label style={labelStyle}>Ansprechpartner*
              <input type="text" name="ansprechpartner" required autoComplete="name" style={fieldStyle} />
            </label>
            <label style={labelStyle}>Ort*
              <input type="text" name="ort" required autoComplete="address-level2" style={fieldStyle} />
            </label>
            <label style={labelStyle}>Telefon*
              <input type="tel" name="telefon" required autoComplete="tel" inputMode="tel" style={fieldStyle} />
            </label>
            <label style={labelStyle}>E-Mail*
              <input type="email" name="email" required autoComplete="email" inputMode="email" style={fieldStyle} />
            </label>
            <label style={labelStyle}>Gewünschte Leistung
              <select name="leistung" defaultValue={prefLeistung} style={fieldStyle}>
                <option value="">Bitte wählen …</option>
                {leistungOptions.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>Objektgröße
              <RangeRow>
                <span style={{ fontSize: '0.9em', color: '#3a4662' }}>ca. Fläche</span>
                <NumberField>
                  <NumberInput
                    type="number"
                    min={20}
                    max={1000}
                    step={10}
                    value={objektgroesse}
                    aria-label="Objektgröße in Quadratmetern"
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v)) setObjektgroesse(Math.min(v, 1000));
                    }}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      setObjektgroesse(Number.isFinite(v) ? Math.min(Math.max(v, 20), 1000) : 80);
                    }}
                  />
                  m²
                </NumberField>
              </RangeRow>
              <Range
                type="range"
                min={20}
                max={1000}
                step={10}
                value={objektgroesse}
                aria-label="Objektgröße in Quadratmetern (Schieberegler)"
                onChange={(e) => setObjektgroesse(Number(e.target.value))}
              />
              <RangeHint>
                <span>20 m²</span>
                <span>1.000 m²</span>
              </RangeHint>
            </label>
            <label style={labelStyle}>Gewünschte Häufigkeit
              <select name="reinigungstage" defaultValue={prefHaeufigkeit} style={fieldStyle}>
                <option value="">Bitte wählen …</option>
                {tageOptions.map((tg) => (
                  <option key={tg} value={tg}>{tg}</option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>Nachricht
              <textarea name="nachricht" rows={5} defaultValue={prefillNachricht} style={{ ...fieldStyle, minHeight: 'unset', resize: 'vertical' }} />
            </label>
            {/* Honeypot-Feld gegen Spam */}
            <input type="text" name="hp" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
            <PrivacyRow>
              <input
                required
                type="checkbox"
                id="privacy"
                name="privacy"
                style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0, accentColor: 'var(--brand)' }}
              />
              <label htmlFor="privacy">
                Ich stimme der Verarbeitung meiner Daten gemäß{' '}
                <PrivacyLink to="/datenschutz" target="_blank">Datenschutzerklärung</PrivacyLink> zu.
              </label>
            </PrivacyRow>
            {status === 'success' && (
              <StatusMessage $variant="success" role="status">
                Vielen Dank! Ihre Anfrage wurde erfolgreich versendet. Wir melden uns zeitnah bei Ihnen.
              </StatusMessage>
            )}
            {status === 'error' && (
              <StatusMessage $variant="error" role="alert">
                Beim Versenden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut – oder
                erreichen Sie uns direkt unter{' '}
                <StatusLink href={PHONE_HREF}>{PHONE_DISPLAY}</StatusLink>{' '}
                beziehungsweise{' '}
                <StatusLink href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  per WhatsApp
                </StatusLink>.
              </StatusMessage>
            )}
            {status === 'invalid' && (
              <StatusMessage $variant="error" role="alert">
                {serverError}
              </StatusMessage>
            )}
            {status === 'ratelimited' && (
              <StatusMessage $variant="error" role="alert">
                Es wurden zu viele Anfragen in kurzer Zeit gesendet. Bitte warten Sie einen Moment und versuchen Sie es erneut.
              </StatusMessage>
            )}
            <Button size="lg" type="submit" disabled={sending} style={{ marginTop: 8 }}>
              {sending ? 'Wird gesendet …' : 'Kostenlose Besichtigung anfragen'}
            </Button>
          </form>
          <div style={{ textAlign: 'center', color: '#3a4662', fontSize: '0.98rem' }}>oder</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <WhatsAppButton size="md" />
          </div>
        </AngebotFormBox>
      </Container>
    </AngebotSection>
  );
};

export default Kontakt;
